import {
  Command,
  Plugin,
  ViewElement,
  ImageUtils,
  ButtonView,
  Locale,
} from "ckeditor5";

import {
  IconInlineBottomLine,
  IconInlineMiddleLine,
  IconInlineTopLine,
} from "@examspark/common-ui/dist/components/icons";

export const UI_BUTTON_IMAGE_INLINE_TOP = "imageStyle:inlineTop";
export const UI_BUTTON_IMAGE_INLINE_MIDDLE = "imageStyle:inlineMiddle";
export const UI_BUTTON_IMAGE_INLINE_BOTTOM = "imageStyle:inlineBottom";

type ImageVerticalAlignment = "top" | "middle" | "bottom";

class ImageAlignVerticalCommand extends Command {
  declare value: ImageVerticalAlignment | undefined;

  override refresh() {
    const imageUtils = this.editor.plugins.get(ImageUtils);
    const element = imageUtils.getClosestSelectedImageElement(
      this.editor.model.document.selection,
    );
    this.isEnabled = element != null;

    if (!imageUtils.isInlineImage(element)) {
      this.value = undefined;
    } else {
      this.value =
        (element?.getAttribute("imageValign") as ImageVerticalAlignment) ??
        "bottom";
    }
  }

  execute(value: ImageVerticalAlignment) {
    const imageUtils = this.editor.plugins.get(ImageUtils);
    const element = imageUtils.getClosestSelectedImageElement(
      this.editor.model.document.selection,
    );

    if (element == null) return;

    this.editor.model.change((writer) => {
      this.editor.execute("imageTypeInline");

      if (value === "bottom") {
        writer.removeAttribute("imageValign", element);
      } else {
        writer.setAttribute("imageValign", value, element);
      }
    });
  }
}

export default class ImageAlignVertical extends Plugin {
  declare imageUtils: ImageUtils;

  init() {
    this.imageUtils = this.editor.plugins.get("ImageUtils");

    this.editor.model.schema.extend("imageInline", {
      allowAttributes: "imageValign",
    });

    const command = new ImageAlignVerticalCommand(this.editor);
    this.editor.commands.add("imageValign", command);

    this.editor.conversion.for("downcast").attributeToAttribute({
      model: {
        name: "imageInline",
        key: "imageValign",
      },
      view: (value: ImageVerticalAlignment, { consumable }, { item }) => {
        consumable.consume(item, "attribute:imageValign");

        if (value === "bottom") return undefined;

        return {
          key: "style",
          value: { "vertical-align": value },
        };
      },
    });

    this.editor.conversion.for("upcast").attributeToAttribute({
      view: {
        name: "img",
        key: "style",
        value: {
          "vertical-align": /top|middle|bottom/,
        },
      },
      model: {
        key: "imageValign",
        value: (element: ViewElement) => {
          const value = element.getStyle("vertical-align") ?? "bottom";

          if (value !== "top" && value !== "middle" && value !== "bottom") {
            return;
          }

          return value === "bottom" ? undefined : value;
        },
      },
    });

    this.editor.ui.componentFactory.add(
      UI_BUTTON_IMAGE_INLINE_BOTTOM,
      this.buttonFactory(command, "bottom", {
        icon: IconInlineBottomLine.Content,
        tooltip: "Inline Bottom Image",
      }),
    );

    this.editor.ui.componentFactory.add(
      UI_BUTTON_IMAGE_INLINE_MIDDLE,
      this.buttonFactory(command, "middle", {
        icon: IconInlineMiddleLine.Content,
        tooltip: "Inline Middle Image",
      }),
    );

    this.editor.ui.componentFactory.add(
      UI_BUTTON_IMAGE_INLINE_TOP,
      this.buttonFactory(command, "top", {
        icon: IconInlineTopLine.Content,
        tooltip: "Inline Top Image",
      }),
    );
  }

  buttonFactory(
    command: ImageAlignVerticalCommand,
    value: ImageVerticalAlignment,
    properties: { icon: string; tooltip: string },
  ) {
    return (locale: Locale) => {
      const button = new ButtonView(locale);
      button.set(properties);
      button.on("execute", () => command.execute(value));
      button.bind("isEnabled").to(command, "isEnabled");
      button.bind("isOn").to(command, "value", (v) => v === value);
      return button;
    };
  }
}
